import { UserAvatar } from "components/UserAvatar";
import LineIcon from "pages/HomePage/components/LineIcon";
import type { IconName } from "pages/HomePage/models";
import React, { type KeyboardEvent, type MouseEvent, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { getTreeViewPath, getUserProfilePath } from "routes/Paths";
import { useSession } from "Session";

import type { ExploreCard, MatchedEntityChip } from "../explore.types";

import styles from "./ExploreTreeCard.module.css";

type ExploreTreeCardProps = {
	card: ExploreCard;
};

const entityTypeIcons: Record<string, IconName> = {
	Concept: "sparkle",
	Creature: "creature",
	Custom: "custom",
	Event: "event",
	Object: "object",
	Organization: "organization",
	Person: "person",
	Place: "place",
	Species: "species"
};

/** A single explore search result: cover, visibility, matched entities, and owner attribution. */
const ExploreTreeCard = ({ card }: ExploreTreeCardProps) => {
	const { t } = useTranslation("explorePage");
	const navigate = useNavigate();
	const { user } = useSession();
	// "Yours" means owned: a tree the user only collaborates on shows its status like any other.
	const isOwner = user !== null && user.id === card.ownerUserId;
	const statusClass = styles[`status${card.status}`] ?? styles.statusPublic;

	// The whole card links to the tree; the owner chip overrides that to open the owner's profile.
	// A nested <a> would be invalid HTML, so the chip navigates programmatically instead.
	const openOwnerProfile = useCallback(
		(event: MouseEvent<HTMLSpanElement> | KeyboardEvent<HTMLSpanElement>): void => {
			event.preventDefault();
			event.stopPropagation();
			navigate(getUserProfilePath(card.ownerUserId));
		},
		[card.ownerUserId, navigate]
	);

	const onOwnerKeyDown = useCallback(
		(event: KeyboardEvent<HTMLSpanElement>): void => {
			if (event.key === "Enter" || event.key === " ") {
				openOwnerProfile(event);
			}
		},
		[openOwnerProfile]
	);

	return (
		<article className={styles.card}>
			<Link className={styles.cardLink} to={getTreeViewPath(card.id)}>
				<div className={styles.coverFrame}>
					<img alt="" className={styles.coverImage} src={card.cover} />
					<span className={`${styles.statusBadge} ${statusClass}`}>
						{isOwner ? t("yourTree") : card.status}
					</span>
				</div>

				<div className={styles.cardBody}>
					<h2 className={styles.cardTitle}>{card.title}</h2>
					<p className={styles.cardDescription}>{card.description}</p>

					{card.matches.length > 0 && (
						<div aria-label={t("matchedEntities")} className={styles.matchList}>
							{card.matches.map((match: MatchedEntityChip) => (
								<span key={match.id} className={styles.matchChip}>
									<LineIcon name={entityTypeIcons[match.type] ?? "sparkle"} />
									{match.label}
								</span>
							))}
						</div>
					)}

					<div className={styles.cardMeta}>
						<span
							className={styles.owner}
							onClick={openOwnerProfile}
							onKeyDown={onOwnerKeyDown}
							role="link"
							tabIndex={0}
							title={t("viewProfile", { name: card.ownerName })}>
							<UserAvatar
								className={styles.ownerAvatar}
								imageUrl={card.ownerImageUrl}
								initials={card.ownerInitials}
							/>
							<span className={styles.ownerName}>{t("byOwner", { name: card.ownerName })}</span>
						</span>

						<span className={styles.starCount} title={t("starCountTitle")}>
							<LineIcon name="star" />
							{card.stars}
						</span>

						<span
							className={
								card.isLiked ? `${styles.likeCount} ${styles.likeCountLiked}` : styles.likeCount
							}>
							<LineIcon name="heart" />
							{card.likes}
						</span>
					</div>

					<div className={styles.cardFooter}>
						<span className={styles.footerFact}>
							<LineIcon name="tree" />
							<span>{t("entityCount", { count: card.entityCount })}</span>
						</span>
						<span className={styles.footerFact}>
							<LineIcon name="clock" />
							<span>{t("lastEdited", { value: card.lastEditedLabel })}</span>
						</span>
					</div>
				</div>
			</Link>
		</article>
	);
};

export default ExploreTreeCard;
